import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FaBold,
  FaItalic,
  FaQuoteRight,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaImage,
  FaLink,
  FaUnderline,
  FaStrikethrough,
  FaAlignJustify,
  FaUndo,
  FaRedo,
} from "react-icons/fa";
import { BiCodeBlock } from "react-icons/bi";
import { MdFontDownload } from "react-icons/md";
import "./RichTextEditor.css";
import { ComboBox } from "@progress/kendo-react-dropdowns";

interface RichTextEditorProps {
  content: string;
  onChange: (value: string) => void;
  maxHeight?: number;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  maxHeight = 500,
  minHeight = 200,
}) => {
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [editorHeight, setEditorHeight] = useState(minHeight);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    align: "left",
    formatBlock: "p",
    orderedList: false,
    unorderedList: false,
  });

  const editorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const isUpdatingContentRef = useRef(false);
  const lastContentRef = useRef("");
  const selectionRef = useRef<Range | null>(null);
  const isResizingRef = useRef(false);

  const headingOptions = [
    { value: "p", label: "Paragraph", size: "14px" },
    { value: "h1", label: "Heading 1", size: "32px" },
    { value: "h2", label: "Heading 2", size: "24px" },
    { value: "h3", label: "Heading 3", size: "20px" },
    { value: "h4", label: "Heading 4", size: "18px" },
    { value: "h5", label: "Heading 5", size: "16px" },
    { value: "h6", label: "Heading 6", size: "14px" },
  ];

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeHandle = containerRef.current.querySelector(
      ".resize-handle"
    ) as HTMLElement;
    if (!resizeHandle) return;

    let startY = 0;
    let startHeight = 0;

    const handleMouseDown = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      mouseEvent.preventDefault();
      isResizingRef.current = true;
      startY = mouseEvent.clientY;
      startHeight = editorHeight;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ns-resize";
    };

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      if (!isResizingRef.current) return;
      const deltaY = mouseEvent.clientY - startY;
      const newHeight = Math.min(
        Math.max(startHeight + deltaY, minHeight),
        maxHeight
      );
      setEditorHeight(newHeight);
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
    };

    resizeHandle.addEventListener("mousedown", handleMouseDown);
    return () => {
      resizeHandle.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [editorHeight, maxHeight, minHeight]);

  const updateActiveFormats = useCallback(() => {
    if (isHtmlMode || !editorRef.current || isUpdatingContentRef.current)
      return;

    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      if (!editorRef.current.contains(range.commonAncestorContainer)) return;

      // Check text formats
      const newFormats = {
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        orderedList: document.queryCommandState("insertOrderedList"),
        unorderedList: document.queryCommandState("insertUnorderedList"),
        align: "left" as string,
        formatBlock: "p" as string,
      };

      // Check alignment
      if (document.queryCommandState("justifyCenter")) {
        newFormats.align = "center";
      } else if (document.queryCommandState("justifyRight")) {
        newFormats.align = "right";
      } else if (document.queryCommandState("justifyFull")) {
        newFormats.align = "justify";
      } else {
        newFormats.align = "left";
      }

      // Check block format
      let element =
        range.commonAncestorContainer.nodeType === 3
          ? range.commonAncestorContainer.parentElement
          : (range.commonAncestorContainer as HTMLElement);

      while (element && element !== editorRef.current) {
        const tagName = element.tagName?.toLowerCase();
        if (
          ["h1", "h2", "h3", "h4", "h5", "h6", "p", "blockquote"].includes(
            tagName
          )
        ) {
          newFormats.formatBlock = tagName;
          break;
        }
        // Also check for list items
        if (tagName === "li") {
          const listParent = element.parentElement;
          if (listParent?.tagName.toLowerCase() === "ol") {
            newFormats.orderedList = true;
          } else if (listParent?.tagName.toLowerCase() === "ul") {
            newFormats.unorderedList = true;
          }
        }
        element = element.parentElement;
      }

      setActiveFormats(newFormats);
    } catch (error) {
      console.warn("Error updating formats:", error);
    }
  }, [isHtmlMode]);

  // Initialize content
  useEffect(() => {
    if (!isHtmlMode && editorRef.current && !isUpdatingContentRef.current) {
      const initialContent = content || "<p><br></p>";

      if (editorRef.current.innerHTML !== initialContent) {
        isUpdatingContentRef.current = true;
        editorRef.current.innerHTML = initialContent;

        if (historyRef.current.length === 0) {
          historyRef.current = [initialContent];
          historyIndexRef.current = 0;
        }

        lastContentRef.current = initialContent;

        setTimeout(() => {
          isUpdatingContentRef.current = false;
        }, 50);
      }
    }

    if (isHtmlMode && textareaRef.current) {
      if (textareaRef.current.value !== content) {
        textareaRef.current.value = content || "";
      }
    }
  }, [isHtmlMode, content]);

  // Handle input events
  const handleInput = useCallback(() => {
    if (!editorRef.current || isUpdatingContentRef.current) return;

    const currentContent = editorRef.current.innerHTML;

    if (lastContentRef.current !== currentContent) {
      lastContentRef.current = currentContent;

      // Add to history
      if (historyRef.current[historyIndexRef.current] !== currentContent) {
        if (historyIndexRef.current < historyRef.current.length - 1) {
          historyRef.current = historyRef.current.slice(
            0,
            historyIndexRef.current + 1
          );
        }

        historyRef.current.push(currentContent);
        historyIndexRef.current = historyRef.current.length - 1;

        if (historyRef.current.length > 50) {
          historyRef.current.shift();
          historyIndexRef.current--;
        }
      }

      onChange(currentContent);
      updateActiveFormats();
    }
  }, [onChange, updateActiveFormats]);

  // Handle selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isUpdatingContentRef.current) {
        updateActiveFormats();
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [updateActiveFormats]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault();
      if (e.shiftKey) {
        redo();
      } else {
        undo();
      }
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key === "y") {
      e.preventDefault();
      redo();
      return;
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    const editor = editorRef.current;
    if (!isHtmlMode && editor) {
      editor.addEventListener("input", handleInput);
      editor.addEventListener("keydown", handleKeyDown);

      return () => {
        editor.removeEventListener("input", handleInput);
        editor.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isHtmlMode, handleInput, handleKeyDown]);

  // Execute command with focus management
  const exec = useCallback(
    (command: string, value?: string) => {
      if (!editorRef.current) return;

      // Ensure editor has focus
      editorRef.current.focus();

      // Restore selection if needed
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      try {
        const result = document.execCommand(command, false, value);
        {
          false && console.log(`Command ${command} executed:`, result);
        }

        // Force update after command
        setTimeout(() => {
          handleInput();
          updateActiveFormats();
        }, 10);
      } catch (error) {
        console.warn("Command execution failed:", error);
      }
    },
    [handleInput, updateActiveFormats]
  );

  // Handle content change for textarea
  const handleTextareaChange = useCallback(() => {
    const value = textareaRef.current?.value || "";
    onChange(value);
  }, [onChange]);

  // Undo functionality
  const undo = useCallback(() => {
    if (historyIndexRef.current > 0 && editorRef.current) {
      isUpdatingContentRef.current = true;
      historyIndexRef.current--;
      editorRef.current.innerHTML = historyRef.current[historyIndexRef.current];
      lastContentRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);

      setTimeout(() => {
        isUpdatingContentRef.current = false;
        updateActiveFormats();
      }, 10);
    }
  }, [onChange, updateActiveFormats]);

  // Redo functionality
  const redo = useCallback(() => {
    if (
      historyIndexRef.current < historyRef.current.length - 1 &&
      editorRef.current
    ) {
      isUpdatingContentRef.current = true;
      historyIndexRef.current++;
      editorRef.current.innerHTML = historyRef.current[historyIndexRef.current];
      lastContentRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);

      setTimeout(() => {
        isUpdatingContentRef.current = false;
        updateActiveFormats();
      }, 10);
    }
  }, [onChange, updateActiveFormats]);

  // Toggle HTML mode
  const toggleHtml = useCallback(() => {
    setIsHtmlMode((prev) => !prev);
    if (!isHtmlMode) {
      setTimeout(() => handleTextareaChange(), 0);
    } else {
      setTimeout(() => handleInput(), 0);
    }
  }, [isHtmlMode, handleTextareaChange, handleInput]);

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (selectionRef.current && editorRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(selectionRef.current);
      editorRef.current.focus();
    }
  }, []);

  const handleHeadingSelect = useCallback(
    (value: string) => {
      restoreSelection();
      exec("formatBlock", `<${value}>`);
    },
    [exec, restoreSelection]
  );

  // Check if undo/redo is available
  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // console.log("activeFormats.formatBlock", activeFormats.formatBlock);

  return (
    <div
      className="rich-text-editor"
      ref={containerRef}
      style={{ height: `${editorHeight + 60}px` }} // 60px for toolbar height
    >
      {/* Enhanced Toolbar */}
      <div className="toolbar">
        {/* Heading Dropdown */}
        <div className="toolbar-group">
          <div className="dropdown-container">
            <ComboBox
              data={headingOptions}
              textField="label"
              value={headingOptions.find(
                (option) => option.value === activeFormats.formatBlock
              )}
              onChange={(e) => {
                const selectedValue = e.value?.value || "p";
                handleHeadingSelect(selectedValue);
              }}
              onOpen={() => {
                saveSelection();
              }}
              onClose={() => {
                setTimeout(restoreSelection, 10);
              }}
              filterable={false}
              size={"small"}
              style={{ width: "140px" }}
            />
          </div>
        </div>
        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("bold")}
            className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`}
            title="Bold"
          >
            <FaBold className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("italic")}
            className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`}
            title="Italic"
          >
            <FaItalic className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("underline")}
            className={`toolbar-btn ${activeFormats.underline ? "active" : ""}`}
            title="Underline"
          >
            <FaUnderline className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("strikeThrough")}
            className={`toolbar-btn ${
              activeFormats.strikethrough ? "active" : ""
            }`}
            title="Strikethrough"
          >
            <FaStrikethrough className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Lists */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("insertOrderedList")}
            className={`toolbar-btn ${
              activeFormats.orderedList ? "active" : ""
            }`}
            title="Numbered List"
          >
            <FaListOl className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("insertUnorderedList")}
            className={`toolbar-btn ${
              activeFormats.unorderedList ? "active" : ""
            }`}
            title="Bulleted List"
          >
            <FaListUl className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("formatBlock", "<blockquote>")}
            className={`toolbar-btn ${
              activeFormats.formatBlock === "blockquote" ? "active" : ""
            }`}
            title="Blockquote"
          >
            <FaQuoteRight className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Alignment */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => exec("justifyLeft")}
            className={`toolbar-btn ${
              activeFormats.align === "left" ? "active" : ""
            }`}
            title="Align Left"
          >
            <FaAlignLeft className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyCenter")}
            className={`toolbar-btn ${
              activeFormats.align === "center" ? "active" : ""
            }`}
            title="Align Center"
          >
            <FaAlignCenter className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyRight")}
            className={`toolbar-btn ${
              activeFormats.align === "right" ? "active" : ""
            }`}
            title="Align Right"
          >
            <FaAlignRight className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("justifyFull")}
            className={`toolbar-btn ${
              activeFormats.align === "justify" ? "active" : ""
            }`}
            title="Justify"
          >
            <FaAlignJustify className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Insert buttons */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => {
              const url = prompt("Enter link URL:");
              if (url) exec("createLink", url);
            }}
            className="toolbar-btn"
            title="Insert Link"
          >
            <FaLink className="icon" />
          </button>

          <button
            type="button"
            onClick={() => {
              const url = prompt("Enter image URL:");
              if (url) exec("insertImage", url);
            }}
            className="toolbar-btn"
            title="Insert Image"
          >
            <FaImage className="icon" />
          </button>

          <button
            type="button"
            onClick={() => exec("insertHorizontalRule")}
            className="toolbar-btn"
            title="Insert Horizontal Rule"
          >
            <BiCodeBlock className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Undo/Redo */}
        <div className="toolbar-group">
          <button
            type="button"
            onClick={undo}
            className={`toolbar-btn ${!canUndo ? "disabled" : ""}`}
            disabled={!canUndo}
            title="Undo"
          >
            <FaUndo className="icon" />
          </button>

          <button
            type="button"
            onClick={redo}
            className={`toolbar-btn ${!canRedo ? "disabled" : ""}`}
            disabled={!canRedo}
            title="Redo"
          >
            <FaRedo className="icon" />
          </button>
        </div>
        <div className="toolbar-divider"></div>

        {/* Toggle HTML */}
        <button
          type="button"
          onClick={toggleHtml}
          className="html-toggle-btn"
          title={isHtmlMode ? "Switch to Rich Text" : "Switch to HTML"}
        >
          {isHtmlMode ? (
            <>
              <MdFontDownload className="icon" />
              Rich Text
            </>
          ) : (
            <>
              <BiCodeBlock className="icon" />
              HTML
            </>
          )}
        </button>
      </div>

      {/* Editor */}
      <div className="editor-area" style={{ height: `${editorHeight}px` }}>
        {isHtmlMode ? (
          <textarea
            ref={textareaRef}
            defaultValue={content}
            onChange={handleTextareaChange}
            className="html-textarea"
            placeholder="Enter your HTML content here..."
          />
        ) : (
          <div
            ref={editorRef}
            className="rich-text-content"
            contentEditable
            suppressContentEditableWarning
          />
        )}
      </div>

      {/* Resize Handle */}
      <div className="resize-handle" title="Drag to resize"></div>
    </div>
  );
};

export default RichTextEditor;
